<template>
    <modal size="lg" @close="reset(); $emit('close')" ref="modal">
        <template v-slot:title>
            <h3 class="modal-title">Earnings Statement</h3>
        </template>

        <slot>
            <template v-if="dataLoaded">
                <div class="mb-3">
                    <h3>Company</h3>
                    <hr class="mt-1 mb-1"/>
                    {{ company.name }}
                </div>

                <div class="mb-3">
                    <h3>Employee</h3>
                    <hr class="mt-1 mb-1"/>
                    <span>{{ employee.full_name }}</span><br/>
                    <span>{{ employee.address_1 }} {{ employee.address_2 }}</span><br/>
                    <span>{{ employee.city }}, {{ employee.state }} {{ employee.postal_code }}</span>
                </div>

                <div>
                    <h3>Current Statement</h3>
                    <hr class="mt-1 mb-1"/>

                    <div class="d-flex justify-content-between">
                        <strong>Check Date:</strong> {{ $filters.date(earningsStatement.check_date) }}
                    </div>
                    <div class="d-flex justify-content-between">
                        <strong>Statement Number:</strong> <span>{{ earningsStatement.earning_statement_number }}</span>
                    </div>

                    <!-- Current Earnings -->
                    <div>
                        <h5 class="mt-4">Current Earnings</h5>
                        <hr class="mt-1 mb-1"/>
                        <div v-for="position in positionsWithCurrentEarnings" :key="position.id" class="mb-1 position">
                            <div class="pb-1"><strong>Position: {{ position.label }}</strong></div>
                            <table class="table css-table widget-table table-fixed">
                                <thead>
                                    <tr>
                                        <th class="pl-0">Earning</th>
                                        <th>Rate</th>
                                        <th class="text-right">Hours/Units</th>
                                        <th class="text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody v-for="earning in position.earnings" class="earning">
                                    <tr>
                                        <td class="truncate-wrapper pl-0">{{ currentEarningName(earning) }}</td>
                                        <td>
                                            <span v-if="earning.calculated_rate && (earning.hours || earning.units)">
                                                {{ $filters.dollars(earning.calculated_rate) }}
                                                <span v-if="earning.hours"> /hr</span>
                                                <span v-else-if="earning.units"> /unit</span>
                                            </span>
                                        </td>
                                        <td class="text-right">{{ currentEarningType(earning) === 'amount' ? '' : ((earning.hours || earning.units) ? Number(earning.hours || earning.units || 0).toFixed(2) : '') }}</td>
                                        <td class="text-right">{{ $filters.dollars(earning.calculated_amount || 0) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="d-flex justify-content-between">
                            <h4>Earnings Total</h4>
                            <h4 class="pr-2">${{ earningsStatementResult.gross_pay }}</h4>
                        </div>
                    </div>

                    <!-- Current Taxes -->
                    <div>
                        <h5 class="mt-4">Taxes</h5>
                        <hr class="mt-1 mb-1"/>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="pl-0">Description</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody v-for="tax in currentTaxes" class="tax">
                                <tr>
                                    <td class="truncate-wrapper pl-0">{{ tax.name.toUpperCase() }}</td>
                                    <td class="text-right">${{ tax.withheld_amount }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-between pt-1">
                            <h4>Taxes Total</h4>
                            <h4 class="pr-2">${{ earningsStatementResult.tax_total }}</h4>
                        </div>
                    </div>

                    <!-- Current Deductions -->
                    <div v-if="deductions.length">
                        <h5 class="mt-4">Deductions</h5>
                        <hr class="mt-1 mb-1"/>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="pl-0">Description</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody v-for="deduction in deductions" class="tax">
                                <tr>
                                    <td class="truncate-wrapper pl-0">{{ deduction.name }}</td>
                                    <td class="text-right">${{ deduction.actual_amount }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-between pt-1">
                            <h4>Deductions Total</h4>
                            <h4 class="pr-2">${{ parseFloat(earningsStatementResult.deductions_total).toFixed(2) }}</h4>
                        </div>
                        <div v-if="deductionNotes.length" class="mt-1">
                            <p v-for="(note, i) in deductionNotes" :key="i" class="small text-muted mb-0">{{ note }}</p>
                        </div>
                    </div>

                    <!-- Current Net Pay -->
                    <div class="paycheck-total d-flex justify-content-between pt-2 pb-2 pl-1 pr-1 mt-1">
                        <h3 class="mb-0">Net Pay</h3>
                        <h3 class="mb-0">${{ earningsStatementResult.net_pay }}</h3>
                    </div>

                    <!-- Time Off -->
                    <div v-if="earningsStatement.accruals && earningsStatement.accruals.length">
                        <h5 class="mt-4">Time Off</h5>
                        <hr class="mt-1 mb-1"/>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="pl-0">Description</th>
                                    <th class="text-right">Accrued</th>
                                    <th class="text-right">Used</th>
                                    <th class="text-right" v-if="hasPaidOut">Paid Out</th>
                                    <th class="text-right">Available Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="accrual in earningsStatement.accruals" :key="accrual.employee_accrual_plan_id">
                                    <td class="truncate-wrapper pl-0">{{ accrual.display_name }}</td>
                                    <td class="text-right">{{ hours(accrual.accrued) }}</td>
                                    <td class="text-right">{{ hours(accrual.used) }}</td>
                                    <td class="text-right" v-if="hasPaidOut">{{ hours(accrual.paid_out || 0) }}</td>
                                    <td class="text-right">{{ hours(accrual.balance) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- YTD -->
                <div v-if="ytd">
                    <h3 class="pt-4 mb-0">Year-to-Date</h3>

                    <div>
                        <h5 class="mt-2">Year-to-Date Earnings</h5>
                        <hr class="mt-1 mb-1"/>
                        <div v-for="p in positionsWithYtdEarningsSorted" :key="p.id" class="mb-1">
                            <div class="pb-1"><strong>Position: {{ p.label }}</strong></div>
                            <table class="table css-table widget-table table-fixed">
                                <thead>
                                    <tr>
                                        <th class="pl-0">Description</th>
                                        <th>Rate</th>
                                        <th class="text-right">Hours/Units</th>
                                        <th class="text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody v-for="earning in ytdEarningsByPositionId[p.id].earnings" class="earning">
                                    <tr>
                                        <td class="truncate-wrapper pl-0">{{ ytdEarningName(earning) }}</td>
                                        <td>
                                            <span v-if="earning.rate && ytdEarningType(earning) !== 'amount'">
                                                {{ $filters.dollars(earning.rate) }}
                                                <span v-if="earning.hours"> /hr</span>
                                                <span v-else-if="earning.units"> /unit</span>
                                            </span>
                                        </td>
                                        <td class="text-right">{{ ytdEarningType(earning) === 'amount' ? '' : earning.hours }}</td>
                                        <td class="text-right">{{ $filters.dollars(earning.amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="d-flex justify-content-between">
                            <h4>Year-to-Date Earnings Total</h4>
                            <h4 class="pr-2">{{ $filters.dollars(ytd.totals.earnings) }}</h4>
                        </div>
                    </div>

                    <div>
                        <h5 class="mt-4">Year-to-Date Taxes</h5>
                        <hr class="mt-1 mb-1"/>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="pl-0">Description</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody v-for="tax in ytd.sym_taxes" class="tax">
                                <tr>
                                    <td class="truncate-wrapper pl-0">{{ tax.description }}</td>
                                    <td class="text-right">{{ $filters.dollars(ytd.item_amounts.taxes[tax.id]) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-between pt-1">
                            <h4>Year-to-Date Taxes Total</h4>
                            <h4 class="pr-2">{{ $filters.dollars(ytd.totals.taxes) }}</h4>
                        </div>
                    </div>

                    <div v-if="(ytd.deferred_comps && ytd.deferred_comps.length) || (ytd.misc_deductions && ytd.misc_deductions.length)">
                        <h5 class="mt-4">Year-to-Date Deductions</h5>
                        <hr class="mt-1 mb-1"/>
                        <table class="table css-table widget-table table-fixed">
                            <thead>
                                <tr>
                                    <th class="pl-0">Description</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody v-for="dc in ytd.deferred_comps" :key="'dc-' + dc.id" class="tax">
                                <tr>
                                    <td class="truncate-wrapper pl-0">{{ dc.deferred_comp_plan.display_name }}</td>
                                    <td class="text-right">{{ $filters.dollars(ytd.item_amounts.deferred_comp[dc.id]) }}</td>
                                </tr>
                            </tbody>
                            <tbody v-for="deduction in ytd.misc_deductions" :key="'misc-' + deduction.id" class="tax">
                                <tr>
                                    <td class="truncate-wrapper pl-0">{{ deduction.company_deduction_type.name }}</td>
                                    <td class="text-right">{{ $filters.dollars(ytd.item_amounts.misc[deduction.id]) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="d-flex justify-content-between pt-1">
                            <h4>Year-to-Date Deductions Total</h4>
                            <h4 class="pr-2">{{ $filters.dollars(parseFloat(ytd.totals.deferred_comps) + parseFloat(ytd.totals.misc_deductions)) }}</h4>
                        </div>
                    </div>

                    <div class="paycheck-total d-flex justify-content-between pt-2 pb-2 pl-1 pr-1 mt-1">
                        <h3 class="mb-0">Year-to-Date Net Pay</h3>
                        <h3 class="mb-0">${{ ytd.totals.net }}</h3>
                    </div>
                </div>
            </template>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">Done</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import ButtonRow from '@/components/ButtonRow'
import ModalMixin from '@/mixins/ModalMixin'
import { makePositionSlug } from '@/utils/payroll'

export default {
    components: { ButtonRow },
    mixins: [ModalMixin],
    props: ['client', 'earningsStatementId'],
    emits: ['close'],
    data() {
        return {
            dataLoaded: false,
            earningsStatement: null,
            earningsStatementResult: null,
            companyEarningTypes: [],
            ytd: null,
            employee: null,
        }
    },
    mounted() {
        this.getData()
    },
    computed: {
        hasPaidOut() {
            return (this.earningsStatement.accruals || []).some(accrual => Number(accrual.paid_out))
        },
        company() {
            if (this.earningsStatement) {
                return this.client.companies.find(c => c.id == this.earningsStatement.company_id) || {}
            }
            return {}
        },
        companyEarningTypesById() {
            return Object.fromEntries(this.companyEarningTypes.map(cet => [cet.id, cet]))
        },
        positionsWithCurrentEarnings() {
            if (!this.earningsStatementResult) return []
            const byPosition = {}
            for (const earning of this.earningsStatementResult.earnings || []) {
                const posId = earning.identifiers?.position_id
                const label = makePositionSlug(this.client.org_level_after_location, earning.identifiers?.org_level_1_value, earning.identifiers?.org_level_2_value, earning.identifiers?.org_level_3_value, earning.identifiers?.org_level_4_value, earning.identifiers?.org_level_5_value) || `Position ${posId}`
                if (!byPosition[posId]) {
                    byPosition[posId] = { id: posId, label, earnings: [] }
                }
                byPosition[posId].earnings.push(earning)
            }
            return Object.values(byPosition).sort((a, b) => a.label.localeCompare(b.label))
        },
        currentTaxes() {
            return (this.earningsStatementResult?.taxes || [])
                .filter(t => !t.is_employer_tax)
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        deductions() {
            return [...(this.earningsStatementResult?.deductions || [])].sort((a, b) => a.name.localeCompare(b.name))
        },
        deductionNotes() {
            return this.deductions.map(d => d.note).filter(note => note)
        },
        ytdEarningsByPositionId() {
            return this.ytd?.combined_earnings_by_position_id || {}
        },
        positionsWithYtdEarningsSorted() {
            return (this.ytd?.positions_with_combined_earnings || [])
                .map(p => ({ ...p, label: `${p.work_location?.name || ''} / ${p.slug}` }))
                .sort((a, b) => a.label.localeCompare(b.label))
        },
    },
    methods: {
        hours(value) {
            return Number(value).toFixed(2)
        },
        currentEarningName(earning) {
            const ids = earning.identifiers || {}
            return this.companyEarningTypesById[ids.company_earning_type_id]?.name || ids.earning_type_slug
        },
        currentEarningType(earning) {
            return this.companyEarningTypesById[earning.identifiers?.company_earning_type_id]?.type
        },
        ytdEarningName(earning) {
            return earning.earning_type_name
        },
        ytdEarningType(earning) {
            return earning.earning_type_type
        },
        getData() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/earnings-statements/${this.earningsStatementId}`)
                .then(resp => {
                    this.earningsStatement = resp.earnings_statement
                    this.employee = resp.employee
                    this.earningsStatementResult = resp.earnings_statement_result
                    this.companyEarningTypes = resp.company_earning_types || []
                    this.ytd = resp.ytd
                    this.dataLoaded = true
                    this.$store.dispatch('STOP_LOADING')
                })
                .catch(() => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError('Could not load earnings statement. Please refresh the page and try again.')
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.paycheck-total {
    background-color: #F7F7F7;
    border: 2px solid #ccc;
}
</style>
